import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LogIn, ArrowLeft } from "lucide-react";

const Login = () => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const data = await login(email, password);
      const role = data?.role;
      if (role === "station_manager") navigate("/station");
      else if (role === "customer") navigate("/my");
      else navigate("/admin");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || t("login_error");
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 min-h-[calc(100vh-5rem)]">
        <aside className="md:col-span-5 lg:col-span-4 bg-[var(--rs-primary)] text-white p-10 lg:p-14 flex flex-col justify-between">
          <div>
            <div className="rs-overline !text-white/60">NIVARA · LOGIN</div>
            <h1 className="font-[Chivo] text-5xl font-black mt-3 leading-[0.95]">
              {t("login_hero_a")}
              <br />
              <span className="text-[var(--rs-accent)]">{t("login_hero_b")}</span>
            </h1>
            <p className="text-white/60 mt-6 max-w-sm">
              {t("login_hero_text")}
            </p>
          </div>
          <div className="font-[Chivo] tracking-[0.4em] text-white/30 text-sm mt-12">NIVARAGROUP.CH</div>
        </aside>
        <div className="md:col-span-7 lg:col-span-8 p-8 md:p-14 flex items-center">
          <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
            <Link to="/" className="text-xs uppercase tracking-[0.2em] text-[var(--rs-text-muted)] hover:text-[var(--rs-text)] inline-flex items-center gap-2" data-testid="login-back-link">
              <ArrowLeft size={12} /> {t("login_back")}
            </Link>
            <h2 className="font-[Chivo] text-3xl font-black mt-4">{t("login_title")}</h2>
            <p className="text-[var(--rs-text-muted)] mt-2">{t("login_sub")}</p>

            <div className="mt-8 space-y-5">
              <label className="block">
                <span className="rs-label">{t("f_email_login")}</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rs-input"
                  required
                  autoComplete="email"
                  data-testid="login-email"
                />
              </label>
              <label className="block">
                <span className="rs-label">{t("f_password")}</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rs-input"
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                />
              </label>
            </div>

            {err && (
              <div className="mt-4 border-l-4 border-[var(--rs-primary)] bg-[var(--rs-primary)]/5 px-4 py-3 text-sm" data-testid="login-error">
                {err}
              </div>
            )}

            <button type="submit" className="rs-btn-primary mt-8 w-full" disabled={busy} data-testid="login-submit">
              <LogIn size={18} className="mr-2" />
              {busy ? "…" : t("btn_login")}
            </button>
            <div className="mt-6 text-sm text-[var(--rs-text-muted)]">
              {t("no_account")} <Link to="/register" className="font-bold underline" data-testid="login-register-link">{t("nav_register")}</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
